import { QuickFiltersPanel, QuickFiltersHeader, QuickFiltersRow, AppliedFiltersRow } from "../../layouts/QuickFilters.jsx";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaFilter, FaPhone, FaEnvelope, FaComment, FaCheck, FaTimes, FaChevronRight, FaChevronLeft, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { IoCallOutline } from "react-icons/io5";
import server from '../../../api/server';
import '../../../dashboard-components/loader.css';
import { useNavigate, useLocation } from 'react-router-dom';
import ModernTimeDropdown from '../../layouts/modernDropdown.jsx';
import CallDetails from './CallDetails.jsx';
import ModernDateRangePicker from '../../layouts/ModernDateRangePicker.jsx';
import { ArrowDown, ArrowUp, ChevronDown, ChevronsUpDown, ChevronUp, Download } from 'lucide-react';
import { useToast } from '../../../context/ToastContext.jsx';
import { IoChatbubbleOutline } from "react-icons/io5";
import { MdOutlineExpandLess, MdOutlineExpandMore } from 'react-icons/md';
import CallAdvancedFiltersPanel from '../../layouts/CallAdvancedFiltersPanel';
import {
  initializeFiltersFromUrl,
  updateUrlWithFilters,
  getListPageFromLocation,
  extractModuleParameterFiltersFromFlatFilters,
  clearCrmLeadCreatedAtFields,
  getCrmLeadCreatedAtApiArgs,
  setCrmLeadCreatedAtPreset,
  buildConversationRecordsStateFromSegment,
  getDashboardUserEmail,
} from '../../../utils/filterUtils';
import CrmLeadCreatedAtFilterChips, { shouldHideCrmLeadCreatedAtChipKey } from "../../layouts/CrmLeadCreatedAtFilterChips";
import CallDownloadParametersModal from './CallDownloadParametersModal.jsx';
import {
  CONVERSATION_RECORD_TYPE_OPTIONS,
  CONVERSATION_TIME_FILTER_OPTIONS,
  getConversationAdvancedFilterDefaults,
  getConversationAdvancedFilterOptionDefaults,
  getConversationQuickFilterDefaults,
  mergeConversationFilterDetails,
} from '../../../utils/conversationFilterModel';
import {
  buildAdditionalTagFilterConditions,
  buildDashboardFilterPayload,
  getAdditionalTagFilterValues,
} from '../../../utils/filterPayloadBuilder.js';

const toSnakeCase = (value) => String(value || '').trim().replace(/([A-Z])/g, '_$1').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
const asValidBetween = (value) => {
  if (!value || typeof value !== 'object') return null;
  const lowerRaw = value.lower;
  const upperRaw = value.upper;
  const lower = lowerRaw === '' || lowerRaw === null || lowerRaw === undefined ? null : Number(lowerRaw);
  const upper = upperRaw === '' || upperRaw === null || upperRaw === undefined ? null : Number(upperRaw);
  const hasLower = lower !== null && !Number.isNaN(lower);
  const hasUpper = upper !== null && !Number.isNaN(upper);
  if (!hasLower && !hasUpper) return null;
  return { lower: hasLower ? lower : null, upper: hasUpper ? upper : null };
};

const buildModuleParameterFilters = (filters = {}, advancedFilters = {}) => {
  const output = [
    ...extractModuleParameterFiltersFromFlatFilters(filters),
    ...buildAdditionalTagFilterConditions(advancedFilters),
  ];
  if (Array.isArray(advancedFilters.agent_feedback_multiple)) {
    advancedFilters.agent_feedback_multiple.forEach((item) => {
      const field = toSnakeCase(item?.agent_feedback_parameter);
      const lower = item?.agent_feedback_parameter_score_lower;
      const upper = item?.agent_feedback_parameter_score_upper;
      if (field && (lower !== null && lower !== undefined && lower !== '' || upper !== null && upper !== undefined && upper !== '')) {
        output.push({ field, operator: 'between', value: { lower, upper }, module: 'agent_feedback' });
      }
    });
  }
  if (Array.isArray(advancedFilters.business_insights_multiple)) {
    advancedFilters.business_insights_multiple.forEach((item) => {
      const field = toSnakeCase(item?.business_insights_parameter);
      const value = item?.business_insights_parameter_value;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'business_insight' });
      } else if (field && value !== null && value !== undefined && value !== '') {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'business_insight' });
      }
    });
  }
  if (advancedFilters.custom_module_parameter_name && advancedFilters.custom_module_parameter_answer !== null && advancedFilters.custom_module_parameter_answer !== undefined && advancedFilters.custom_module_parameter_answer !== '') {
    const betweenValue = asValidBetween(advancedFilters.custom_module_parameter_answer);
    output.push({
      field: toSnakeCase(advancedFilters.custom_module_parameter_name),
      operator: betweenValue ? 'between' : Array.isArray(advancedFilters.custom_module_parameter_answer) ? 'in' : 'eq',
      value: betweenValue || advancedFilters.custom_module_parameter_answer,
      module: 'custom_module'
    });
  }
  if (Array.isArray(advancedFilters.custom_module_multiple)) {
    advancedFilters.custom_module_multiple.forEach((item) => {
      const field = toSnakeCase(item?.custom_module_parameter_name);
      const value = item?.custom_module_parameter_answer;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'custom_module' });
      } else if (field && value !== null && value !== undefined && value !== '') {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'custom_module' });
      }
    });
  }
  const dedupe = [];
  const seen = new Set();
  output.forEach((item) => {
    const key = JSON.stringify([item.field, item.operator, item.module, item.value]);
    if (seen.has(key)) return;
    seen.add(key);
    dedupe.push(item);
  });
  return dedupe;
};

const buildParameterQueryFilters = (conditions = []) => {
  const query = {};
  conditions.forEach((condition) => {
    if (!condition.module) return;
    const keyPrefix = condition.module ? `${condition.module}_${condition.field}` : condition.field;
    if (condition.operator === 'between' && condition.value && typeof condition.value === 'object') {
      const lowerKey = condition.module === 'agent_feedback' ? `${keyPrefix}_score_lower` : `${keyPrefix}_lower`;
      const upperKey = condition.module === 'agent_feedback' ? `${keyPrefix}_score_upper` : `${keyPrefix}_upper`;
      if (condition.value.lower !== undefined && condition.value.lower !== null && condition.value.lower !== '') {
        query[lowerKey] = condition.value.lower;
      }
      if (condition.value.upper !== undefined && condition.value.upper !== null && condition.value.upper !== '') {
        query[upperKey] = condition.value.upper;
      }
      return;
    }
    if (condition.value !== undefined && condition.value !== null && condition.value !== '') {
      query[keyPrefix] = condition.value;
    }
  });
  return query;
};

const ConversationRecords = ({ serviceID, type, clientID, segment, email: emailProp, onStaleDrilldown }) => {
  const isEmbeddedModal = type === 'modal-open';
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const exactCountDrilldown = segment?.custom_report_drilldown || null;
  const appliedFilterSummary = Array.isArray(segment?.applied_filter_summary)
    ? segment.applied_filter_summary
    : [];

  const [filterMode, setFilterMode] = useState("quick");
  const [conversationIds, setConversationIds] = useState([]);
  const [customerIds, setCustomerIds] = useState([]);

  // Default filter values
  const defaultFilters = {
    ...getConversationQuickFilterDefaults(),
    ...(exactCountDrilldown ? { time: '' } : {}),
  };
  const defaultAdvancedFilters = getConversationAdvancedFilterDefaults();

  // Initialize filters from URL or use defaults (only when not passed via segment prop)
  const initialFilterState = !segment ? initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters) : {
    filters: defaultFilters,
    advancedFilters: defaultAdvancedFilters,
    customStart: '',
    customEnd: ''
  };

  const [email, setEmail] = useState(() => emailProp || getDashboardUserEmail())
  const [segmentFiltersReady, setSegmentFiltersReady] = useState(!segment)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0)
  const [fatalCalls, setFatalCalls] = useState([])
  const [showFatalCalls, setShowFatalCalls] = useState(false)
  const [originalSize, setOriginalSize] = useState(0)
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const [openType, setOpenType] = useState(null)
  const [selectedCallID, setSelectedCallID] = useState(null)
  const [segmentParameterData, setSegmentParameterData] = useState({'type':null, 'id': null, 'name': null})
  const [expandedSections, setExpandedSections] = useState({
    agentFeedback: false,
    businessInsights: false,
    concernsAndQueries: false,
    dispositionAndSubdisposition: false
  });
  const [filters, setFilters] = useState(initialFilterState.filters);
  const [advancedFilters, setAdvancedFilters] = useState(initialFilterState.advancedFilters);
  const [page, setPage] = useState(() => (!segment && !type ? getListPageFromLocation(location) : 1));
  const [totalPages, setTotalPages] = useState(1);
  const [apiCallRunning, setApiCallRunning] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dummyFilterData, setDummyFilterData] = useState({
    employees: [],
    modes: CONVERSATION_RECORD_TYPE_OPTIONS,
    times: CONVERSATION_TIME_FILTER_OPTIONS,
  });
  const [multiSelectFields, setMultiSelectFields] = useState([]);
  const isMultiSelectField = (key) => Array.isArray(multiSelectFields) && multiSelectFields.includes(key);
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  let [dummyAdvancedFilterData, setDummyAdvancedFilterData] = useState(getConversationAdvancedFilterOptionDefaults)
  const [showConversationPage, setShowConversationPage] = useState(false)
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: null })
  const [fatalSortConfig, setFatalSortConfig] = useState({ field: null, direction: null })
  
  // Update URL whenever filters change (only if not using segment prop)
  useEffect(() => {
    if (!segment && !type && client_id && service_id) { // Only update URL for standalone usage, not when embedded as modal
      // Debounce URL updates to avoid conflicts with navigation
      const timeoutId = setTimeout(() => {
        const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
        const queryParameterFilters = buildParameterQueryFilters(moduleParameterFilters);
        const advancedFiltersForUrl = { ...advancedFilters };
        delete advancedFiltersForUrl.agent_feedback_multiple;
        delete advancedFiltersForUrl.business_insights_multiple;
        delete advancedFiltersForUrl.custom_module_multiple;
        delete advancedFiltersForUrl.custom_module_parameter_name;
        delete advancedFiltersForUrl.custom_module_parameter_answer;
        updateUrlWithFilters(
          navigate,
          location,
          { ...filters, ...queryParameterFilters },
          advancedFiltersForUrl,
          filters.customStart,
          filters.customEnd,
          page
        );
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [filters, advancedFilters, segment, type, client_id, service_id, page]); // Removed navigate and location from deps

  // Restore list page from URL when using browser back/forward
  useEffect(() => {
    if (segment || type) return;
    const fromUrl = getListPageFromLocation(location);
    setPage((p) => (p !== fromUrl ? fromUrl : p));
  }, [location.search, segment, type]);

  function getCookie(name) {
    function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
}
  const getAudioDurationValueForApi = (valueObj = advancedFilters) => {
    if (valueObj.audioDuration !== 'Custom Range') {
      return valueObj.audioDuration;
    }
    const lower = valueObj.audioDurationLower;
    const upper = valueObj.audioDurationUpper;
    if (lower === null || lower === undefined || lower === '' || upper === null || upper === undefined || upper === '') {
      return '';
    }
    const lowerNum = Number(lower);
    const upperNum = Number(upper);
    if (Number.isNaN(lowerNum) || Number.isNaN(upperNum) || lowerNum < 0 || upperNum < 0 || lowerNum > upperNum) {
      return '';
    }
    return `${lowerNum}-${upperNum}`;
  };

  const buildExactAdditionalFilters = () => {
    const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
    const [crmLeadCreatedAt, crmLeadCreatedAtStart, crmLeadCreatedAtEnd, crmLeadCreatedAtRangeType] =
      getCrmLeadCreatedAtApiArgs(advancedFilters);
    return buildDashboardFilterPayload({
      employee_id: filters.employee,
      team_name: filters.team_name,
      record_type: filters.record_type,
      call_time: filters.time,
      custom_start: filters.customStart,
      custom_end: filters.customEnd,
      custom_range_type: filters.customRangeType,
      audio_duration: getAudioDurationValueForApi(),
      customer_id: advancedFilters.customerId,
      open_concerns: advancedFilters.openConcerns,
      conversation_id: filters.conversationId,
      score_lower: advancedFilters.scoreLower,
      score_upper: advancedFilters.scoreUpper,
      fatal: advancedFilters.fatal,
      conversation_potential: advancedFilters.conversationPotential,
      conversation_type: advancedFilters.type,
      conversation_direction: advancedFilters.conversation_direction,
      lead_source: advancedFilters.lead_source,
      primary_language: advancedFilters.primary_language,
      secondary_languages: advancedFilters.secondary_languages,
      lead_sub_source: advancedFilters.lead_sub_source,
      tag: advancedFilters.tag,
      tag2: advancedFilters.tag2,
      tag3: advancedFilters.tag3,
      tag4: advancedFilters.tag4,
      tag5: advancedFilters.tag5,
      tag6: advancedFilters.tag6,
      tag7: advancedFilters.tag7,
      tag8: advancedFilters.tag8,
      tag9: advancedFilters.tag9,
      tag10: advancedFilters.tag10,
      tag11: advancedFilters.tag11,
      tag12: advancedFilters.tag12,
      tag13: advancedFilters.tag13,
      tag14: advancedFilters.tag14,
      tag15: advancedFilters.tag15,
      tag16: advancedFilters.tag16,
      tag17: advancedFilters.tag17,
      tag18: advancedFilters.tag18,
      tag19: advancedFilters.tag19,
      tag20: advancedFilters.tag20,
      tag21: advancedFilters.tag21,
      tag22: advancedFilters.tag22,
      tag23: advancedFilters.tag23,
      tag24: advancedFilters.tag24,
      tag25: advancedFilters.tag25,
      tag26: advancedFilters.tag26,
      tag27: advancedFilters.tag27,
      tag28: advancedFilters.tag28,
      tag29: advancedFilters.tag29,
      tag30: advancedFilters.tag30,
      campaign_id: advancedFilters.campaignId,
      campaign_batch_id: advancedFilters.campaignBatchId,
      actor_type: advancedFilters.actorType,
      first_proper_conversation: advancedFilters.first_proper_conversation,
      needs_review: advancedFilters.needsReview,
      crm_lead_stage: advancedFilters.crm_lead_stage,
      crm_lead_sub_stage: advancedFilters.crm_lead_sub_stage,
      customer_phone_number: advancedFilters.customer_phone_number,
      crm_location: advancedFilters.crm_location,
      conversation_type_reason: advancedFilters.conversation_type_reason,
      customer_outcome: advancedFilters.conversationOutcome,
      refusal_category: advancedFilters.refusalCategory,
      crm_lead_created_at: crmLeadCreatedAt,
      crm_lead_created_at_start: crmLeadCreatedAtStart,
      crm_lead_created_at_end: crmLeadCreatedAtEnd,
      crm_lead_created_at_range_type: crmLeadCreatedAtRangeType,
      module_parameter_filters: moduleParameterFilters,
    }).filter || { and: [] };
  };

  const downloadConversations = async (parametersJson, filterMode, convoIds = [], customerIds = []) => {
    showToast("We're preparing your download. You'll receive an email with the link shortly.", "success");
    try {
      const requestEmail = resolveRequestEmail();
      if (!requestEmail) return;
      const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
      const res = exactCountDrilldown
        ? await server.getCustomReportDrilldown({
            client_id,
            service_id,
            email: requestEmail,
            menu: 'custom-reports',
            ...exactCountDrilldown,
            additional_filters: buildExactAdditionalFilters(),
            perform_download: true,
            parameters: parametersJson,
          })
        : await server.getDownloadCallData(
        client_id,
        service_id,
        requestEmail,
        'conversations',
        undefined,
        1,
        filters.employee,
        filters.record_type,
        filters.time,
        getAudioDurationValueForApi(),
        filters.customStart,
        filters.customEnd,
        segmentParameterData['type'],
        segmentParameterData['id'],
        segmentParameterData['name'],
        advancedFilters.customerId,
        advancedFilters.openConcerns,
        filters.conversationId,
        advancedFilters.scoreLower,
        advancedFilters.scoreUpper,
        'downloads',
        totalRecords,
        advancedFilters.fatal,
        advancedFilters.conversationPotential,
        advancedFilters.type,
        advancedFilters.agent_feedback_multiple,
        advancedFilters.business_insights_multiple,
        advancedFilters.concernCategory,
        advancedFilters.concernSubcategory,
        advancedFilters.queryCategory,
        advancedFilters.querySubcategory,
        advancedFilters.disposition,
        advancedFilters.subdisposition,
        advancedFilters.conversation_direction,
        advancedFilters.lead_source,
        advancedFilters.primary_language,
        advancedFilters.secondary_languages,
        advancedFilters.lead_sub_source,
        advancedFilters.tag,
        advancedFilters.tag2,
        advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters),
        advancedFilters.campaignId,
        advancedFilters.campaignBatchId,
        advancedFilters.actorType,
        advancedFilters.first_proper_conversation,
        advancedFilters.needsReview,
        advancedFilters.crm_lead_stage,
        advancedFilters.crm_lead_sub_stage,
        advancedFilters.customer_phone_number,
        advancedFilters.crm_location,
        advancedFilters.conversation_type_reason,
        advancedFilters.custom_module_parameter_name,
        advancedFilters.custom_module_parameter_answer,
        undefined,
        parametersJson,
        true,
        moduleParameterFilters,
        ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name,
        filterMode,
        convoIds,
        customerIds
      );
      if (!res.ok) {
        let message = 'Could not queue your download. Please try again later';
        try {
          const payload = await res.json();
          message = payload?.message || payload?.error || message;
          if (exactCountDrilldown && ["stale_drilldown_context", "expired_drilldown_context"].includes(payload?.error)) {
            onStaleDrilldown?.(payload.error);
          }
        } catch (error) {
          // Keep the fallback when the response is not JSON.
        }
        showToast(message, 'error');
      }
    } catch (error) {
      console.error("Download conversations failed:", error);
      showToast('Something went wrong. Please try again later', 'error');
    }
  };
 
  const formatEpochToReadableDate = (epoch) => {
    const date = new Date(epoch*1000); // Convert seconds to milliseconds
    const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
    return date.toLocaleString('en-GB', options).replace(',', '');
  };

  const [conversations, setConversations] = useState([]);
  // Preserve scroll position of the conversations list across opening/closing details
  const tableScrollRef = useRef(null);
  const savedScrollTopRef = useRef(0);

  const handleQuickFilterChange = (e, key) => {
    const rawValue = e.target.value;
    const value = isMultiSelectField(key)
      ? (Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []))
      : rawValue;
    
    // If changing time filter and it's not "Custom Range", clear the custom date values
    if (key === 'time' && !Array.isArray(value) && value !== 'Custom Range') {
      setFilters((prev) => ({ 
        ...prev, 
        [key]: value,
        customRangeType: 'between',
        customStart: '',
        customEnd: ''
      }));
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };
  const handleAdvancedInputChange = (e, key) => {
    const value = e.target.value;
    if (key === 'crm_lead_created_at') {
      setAdvancedFilters((prev) => setCrmLeadCreatedAtPreset(prev, value));
      return;
    }
    if (key === 'audioDuration' && value !== 'Custom Range') {
      setAdvancedFilters((prev) => ({
        ...prev,
        audioDuration: value,
        audioDurationLower: null,
        audioDurationUpper: null,
      }));
      return;
    }
    setAdvancedFilters((prev) => ({ ...prev, [key]: value }));
  };
  const callsAbortRef = useRef(null);

  const resolveRequestEmail = () => emailProp || email || getDashboardUserEmail();

  const get_calls_page_details = async (client_id, service_id, page_size) => {
    const requestEmail = resolveRequestEmail();
    if (!requestEmail) return;
    // If another request exists, abort it; we'll launch a new one immediately
    if(callsAbortRef.current){
      callsAbortRef.current.abort();
    }
    const controller = new AbortController();
    callsAbortRef.current = controller;

    try {
      setApiCallRunning(true)
      setLoading(true)
      const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);

      let res = exactCountDrilldown
        ? await server.getCustomReportDrilldown({
            client_id,
            service_id,
            email: requestEmail,
            menu: 'custom-reports',
            ...exactCountDrilldown,
            additional_filters: buildExactAdditionalFilters(),
            pagination: { page_number: page_size },
          }, controller.signal)
        : await server.getCallsPageDetails(client_id, service_id, requestEmail, 'conversations', undefined, page_size,filters.employee, filters.record_type, filters.time, getAudioDurationValueForApi(),
        filters.customStart, filters.customEnd, advancedFilters.customerId, advancedFilters.scoreLower, advancedFilters.scoreUpper, 
        advancedFilters.openConcerns, filters.conversationId, segmentParameterData['type'], segmentParameterData['id'], 
        segmentParameterData['name'], advancedFilters.fatal, advancedFilters.conversationPotential, advancedFilters.type, advancedFilters.agent_feedback_multiple, advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition,
        advancedFilters.conversation_direction, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.campaignId, advancedFilters.campaignBatchId, advancedFilters.actorType, undefined, advancedFilters.first_proper_conversation, advancedFilters.needsReview, advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.conversation_type_reason, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name, advancedFilters.conversationOutcome, advancedFilters.refusalCategory)
        if(res.status === 200){
          let resp = await res.json()
          setConversations(resp.data)
          setFilteredConversations(resp.data)
          const totalRecords = resp.total_records;
          setTotalRecords(totalRecords)
          setOriginalSize(totalRecords)
          setTotalPages(Math.ceil(totalRecords / 50));
          setApiCallRunning(false)
          // Stop page-level loading as soon as call-data arrives
          setLoading(false);
        }else{
          let message = 'Something went wrong. Please try again later';
          try {
            const payload = await res.json();
            message = payload?.message || payload?.error || message;
            if (exactCountDrilldown && ["stale_drilldown_context", "expired_drilldown_context"].includes(payload?.error)) {
              onStaleDrilldown?.(payload.error);
            }
          } catch (error) {
            // Keep the fallback when the response is not JSON.
          }
          showToast(message, 'error')
          setApiCallRunning(false)
          setLoading(false)
        }
    } catch (error) {
      if(error.name === 'AbortError'){
        setApiCallRunning(false);
        return; // silently ignore aborted request
      }
      setApiCallRunning(false)
      setLoading(false)
    }
  }

  // Fetch quick-filter dropdown data once at mount or when IDs/email set
  const fetchFilterDetailsOnce = async () => {
    const requestEmail = resolveRequestEmail();
    if (!requestEmail) return;
    try {
      const res = await server.getAdvancedFilterDetailsAgentFeedback(client_id, service_id, requestEmail, 'home', undefined);
      if(res.status === 200){
        const data = await res.json();
        setMultiSelectFields(Array.isArray(data.multiselects_list) ? data.multiselects_list : []);
        setDummyAdvancedFilterData(prev => mergeConversationFilterDetails(prev, data));
      }
    } catch(err) {  }
  }

  // Function to get disposition keys (for the disposition dropdown)
  const getDispositionKeys = () => {
    if (!dummyAdvancedFilterData.subdisposition || typeof dummyAdvancedFilterData.subdisposition !== 'object') {
      return dummyAdvancedFilterData.disposition || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.subdisposition);
    return keys;
  };

  const getSubdispositionsForDisposition = (selectedDisposition) => {
    if (!dummyAdvancedFilterData.subdisposition) {
      return [];
    }
    
    // If no disposition selected, return all subdispositions
    if (!selectedDisposition) {
      if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
        // Flatten all subdispositions from all dispositions
        return Object.values(dummyAdvancedFilterData.subdisposition).flat();
      }
      return dummyAdvancedFilterData.subdisposition || [];
    }
    
    if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
      const subdispositions = dummyAdvancedFilterData.subdisposition[selectedDisposition] || [];
      return subdispositions;
    }
    
    return dummyAdvancedFilterData.subdisposition || [];
  };

  // Enhanced handler for disposition changes
  const handleDispositionChange = (e) => {
    const newDisposition = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      disposition: newDisposition
    }));
    
    if (newDisposition) {
      const validSubdispositions = getSubdispositionsForDisposition(newDisposition);
      const currentSubdisposition = advancedFilters.subdisposition;
      
      // If current subdisposition is not valid for new disposition, clear it
      if (currentSubdisposition && !validSubdispositions.includes(currentSubdisposition)) {
        setAdvancedFilters(prev => ({
          ...prev,
          subdisposition: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        subdisposition: ''
      }));
    }
  };
  // Function to get concern category keys (for the concern category dropdown)
  const getConcernCategoryKeys = () => {
    if (!dummyAdvancedFilterData.concern_subcategories || typeof dummyAdvancedFilterData.concern_subcategories !== 'object') {
      return dummyAdvancedFilterData.concern_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.concern_subcategories);
    return keys;
  };

  const getConcernSubcategoriesForCategory = (selectedConcernCategory) => {
    if (!dummyAdvancedFilterData.concern_subcategories) {
      return [];
    }
    
    // If no concern category selected, return all subcategories
    if (!selectedConcernCategory) {
      if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.concern_subcategories).flat();
      }
      return dummyAdvancedFilterData.concern_subcategories || [];
    }
    
    if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
      const subcategories = dummyAdvancedFilterData.concern_subcategories[selectedConcernCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.concern_subcategories || [];
  };

  // Enhanced handler for concern category changes
  const handleConcernCategoryChange = (e) => {
    const newConcernCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      concernCategory: newConcernCategory
    }));
    
    if (newConcernCategory) {
      const validSubcategories = getConcernSubcategoriesForCategory(newConcernCategory);
      const currentSubcategory = advancedFilters.concernSubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          concernSubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        concernSubcategory: ''
      }));
    }
  };

  // Function to get query category keys (for the query category dropdown)
  const getQueryCategoryKeys = () => {
    if (!dummyAdvancedFilterData.query_subcategories || typeof dummyAdvancedFilterData.query_subcategories !== 'object') {
      return dummyAdvancedFilterData.query_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.query_subcategories);
    return keys;
  };

  const getQuerySubcategoriesForCategory = (selectedQueryCategory) => {
    if (!dummyAdvancedFilterData.query_subcategories) {
      return [];
    }
    
    // If no query category selected, return all subcategories
    if (!selectedQueryCategory) {
      if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.query_subcategories).flat();
      }
      return dummyAdvancedFilterData.query_subcategories || [];
    }
    
    if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
      const subcategories = dummyAdvancedFilterData.query_subcategories[selectedQueryCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.query_subcategories || [];
  };
  // Custom Module helpers
  const getCustomModuleKeys = () => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module)) return [];
    return dummyAdvancedFilterData.custom_module.map(([key]) => key);
  };
  const getCustomModuleValues = (selectedParameter) => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module) || !selectedParameter) return [];
    const found = dummyAdvancedFilterData.custom_module.find(([key]) => key === selectedParameter);
    return found ? (Array.isArray(found[1]) ? found[1] : []) : [];
  };
  const handleCustomModuleParameterChange = (e) => {
    const param = e.target.value;
    setAdvancedFilters(prev => ({
      ...prev,
      custom_module_parameter_name: param,
      custom_module_parameter_answer: getCustomModuleValues(param).includes(prev.custom_module_parameter_answer) ? prev.custom_module_parameter_answer : ''
    }));
  };

  // Enhanced handler for query category changes
  const handleQueryCategoryChange = (e) => {
    const newQueryCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      queryCategory: newQueryCategory
    }));
    
    if (newQueryCategory) {
      const validSubcategories = getQuerySubcategoriesForCategory(newQueryCategory);
      const currentSubcategory = advancedFilters.querySubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          querySubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        querySubcategory: ''
      }));
    }
  };

  useEffect(() => {
    if (emailProp) setEmail(emailProp);
  }, [emailProp]);

  useEffect(() => {
    if (client_id && service_id && resolveRequestEmail()) {
      fetchFilterDetailsOnce();
    }
  }, [client_id, service_id, email, emailProp]);
  const getBusinessInsightValues = (selectedParameter) => {
    if (!selectedParameter || !dummyAdvancedFilterData.business_insights_parameters) return [];
    
    // If business_insights_parameters is an object with key-value pairs
    if (typeof dummyAdvancedFilterData.business_insights_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      // Convert display name back to key format
      const keyFormat = selectedParameter.toLowerCase().replace(/ /g, '_');
      return dummyAdvancedFilterData.business_insights_parameters[keyFormat] || [];
    }
    
    // If business_insights_parameters is an array of arrays like [['key', ['values']], ...]
    if (Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      const selectedInsight = dummyAdvancedFilterData.business_insights_parameters.find(
        ([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) === selectedParameter
      );
      return selectedInsight ? selectedInsight[1] : [];
    }
    
    return [];
  };

  // Get business insight parameter keys (formatted for display)
  const getBusinessInsightKeys = () => {
    if (!dummyAdvancedFilterData.business_insights_parameters) return [];
    
    // If it's an object, get the keys
    if (typeof dummyAdvancedFilterData.business_insights_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      return Object.keys(dummyAdvancedFilterData.business_insights_parameters).map(key => 
        key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      );
    }
    
    // If it's an array of arrays, get the first element of each sub-array
    if (Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      return dummyAdvancedFilterData.business_insights_parameters.map(([key]) => 
        key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      );
    }
    
    return [];
  };

  const handleCallDetail = (id) => {
    // Save current scroll position before opening details
    if (tableScrollRef.current) {
      savedScrollTopRef.current = tableScrollRef.current.scrollTop || 0;
    }
    if (openType === 'modal-open'){
      setSelectedCallID(id)
      setShowConversationPage(true);
      return;
    }
    if (!segment && !type && client_id && service_id) {
      const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
      const queryParameterFilters = buildParameterQueryFilters(moduleParameterFilters);
      const advancedFiltersForUrl = { ...advancedFilters };
      delete advancedFiltersForUrl.agent_feedback_multiple;
      delete advancedFiltersForUrl.business_insights_multiple;
      delete advancedFiltersForUrl.custom_module_multiple;
      delete advancedFiltersForUrl.custom_module_parameter_name;
      delete advancedFiltersForUrl.custom_module_parameter_answer;
      updateUrlWithFilters(
        navigate,
        location,
        { ...filters, ...queryParameterFilters },
        advancedFiltersForUrl,
        filters.customStart,
        filters.customEnd,
        page
      );
    }
    const encodedCallId = encodeURIComponent(id);
    let url = `/analytics/${client_id}/${service_id}/conversations/${encodedCallId}`
    navigate(url)
  }
  const handleClose = (e) => {
    setShowConversationPage(false);
    setSelectedCallID(null);
  }

  // After conversation page closes and list remounts, restore scroll
  useEffect(() => {
    if (!showConversationPage && tableScrollRef.current) {
      tableScrollRef.current.scrollTop = savedScrollTopRef.current || 0;
    }
  }, [showConversationPage]);

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  const sliderTrackRef = useRef(null);
  const isDraggingLower = useRef(false);
  const isDraggingUpper = useRef(false);
  // Ref to debounce filter change API calls
  const filtersDebounceRef = useRef(null);
  const firstFiltersLoadRef = useRef(true); // to skip debounce on initial mount
  const isInitializingRef = useRef(false); // to prevent API calls during initialization
  
  // Handle mouse events for both thumbs
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!sliderTrackRef.current || (!isDraggingLower.current && !isDraggingUpper.current)) return;
      
      const rect = sliderTrackRef.current.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const percentage = Math.min(Math.max((offsetX / rect.width) * 100, 0), 100);
      
      if (isDraggingLower.current) {
        const newValue = Math.min(percentage, advancedFilters.scoreUpper - 1);
        setAdvancedFilters(prev => ({ ...prev, scoreLower: newValue }));
      } else if (isDraggingUpper.current) {
        const newValue = Math.max(percentage, advancedFilters.scoreLower + 1);
        setAdvancedFilters(prev => ({ ...prev, scoreUpper: newValue }));
      }
    };

    const handleMouseUp = () => {
      isDraggingLower.current = false;
      isDraggingUpper.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [advancedFilters.scoreLower, advancedFilters.scoreUpper]);
  
  const handleNumberInputChange = (e, type) => {
    const value = e.target.value;
    
    // if (isNaN(value)) return;
    
    if (type === 'scoreLower') {
      setAdvancedFilters(prev => ({ ...prev, scoreLower: value}));
    } else if (type === 'scoreUpper') {
      setAdvancedFilters(prev => ({ ...prev, scoreUpper: value }));
    } else if(type === 'agentFeedbackParameterScoreLower'){
      setAdvancedFilters(prev => ({...prev, agentFeedbackParameterScoreLower: value}))
    }else if(type === 'agentFeedbackParameterScoreUpper'){
      setAdvancedFilters(prev => ({...prev, agentFeedbackParameterScoreUpper: value}))
    }
  };

  useEffect(() => {
    isInitializingRef.current = true;
    setSegmentFiltersReady(false);

    const resolvedEmail = emailProp || getDashboardUserEmail();
    if (resolvedEmail) setEmail(resolvedEmail);
    if (serviceID) setService_id(serviceID);
    if (clientID) setClient_id(clientID);
    if (type) setOpenType(type);

    if (segment) {
      setSegmentParameterData(segment);
      const { filters: segmentFilters, advancedFilters: segmentAdvancedFilters } =
        buildConversationRecordsStateFromSegment(segment, defaultFilters, defaultAdvancedFilters);
      setFilters(segmentFilters);
      setAdvancedFilters(segmentAdvancedFilters);
    }

    if (role === 'employee') {
      const user = getCookie('dashboard_login_user_creds')
        ? JSON.parse(getCookie('dashboard_login_user_creds'))
        : null;
      if (user?.name) {
        setFilters((prev) => ({ ...prev, employee: user.name }));
      }
    }

    setSegmentFiltersReady(true);
    isInitializingRef.current = false;
  }, [serviceID, clientID, role, segment, emailProp]);


  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(!showAdvancedFilters);
  };

  const getScoreBar = (score) => {
    if(score === null){
      return ""
    }
    let color = 'bg-green-500';
    let width = `${score}%`;
    
    
    
    return (
      <div className="w-16 bg-gray-200 rounded h-2">
        <div className={`h-2 rounded ${color}`} style={{ width }}></div>
      </div>
    );
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'call':
        return <IoCallOutline className="text-blue-700" />;
      case 'email':
        return <FaEnvelope className="text-blue-500" />;
      case 'chat':
        return <IoChatbubbleOutline className="text-blue-700" />;
      default:
        return <IoCallOutline className="text-blue-700" />;
    }
  };

  const parseDurationToSeconds = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    const str = String(value);
    if (str.includes(':')) {
      const parts = str.split(':').map((p) => Number(p));
      if (parts.some((n) => Number.isNaN(n))) return null;
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      if (parts.length === 2) return parts[0] * 60 + parts[1];
      if (parts.length === 1) return parts[0];
    }
    const num = Number(str);
    return Number.isNaN(num) ? null : num;
  };

  const getValueForSort = (item, field) => {
    if (!item) return null;
    switch (field) {
      case 'time':
      case 'record_time':
      case 'processedat':
        return item[field] != null ? Number(item[field]) : null;
      case 'score':
        return item.score != null ? Number(item.score) : null;
      case 'duration':
        return parseDurationToSeconds(item.duration);
      case 'fatal': {
        const v = item.fatal;
        if (typeof v === 'boolean') return v ? 1 : 0;
        if (v == null) return null;
        const s = String(v).toLowerCase();
        if (s === 'yes') return 1;
        if (s === 'no') return 0;
        return s;
      }
      case 'customerid': {
        const v = item.customerid ?? item.customer_id;
        if (v == null) return null;
        return typeof v === 'number' ? v : String(v).toLowerCase();
      }
      default: {
        const v = item[field];
        if (v == null) return null;
        return typeof v === 'number' ? v : String(v).toLowerCase();
      }
    }
  };

  const sortArray = (list, field, direction) => {
    if (!field || !direction) return list;
    const arr = [...list];
    const factor = direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = getValueForSort(a, field);
      const vb = getValueForSort(b, field);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls last
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return factor * (va - vb);
      }
      return factor * String(va).localeCompare(String(vb));
    });
    return arr;
  };

  const arrowClass = (cfg, field, dir) => (cfg.field === field && cfg.direction === dir ? 'text-indigo-600' : 'text-gray-300 group-hover:text-gray-400');
  const getAriaSort = (cfg, field) => (cfg.field === field ? (cfg.direction === 'asc' ? 'ascending' : 'descending') : 'none');
  const cycleSort = (field, table) => {
    const cfg = table === 'fatal' ? fatalSortConfig : sortConfig;
    let nextDir = 'asc';
    if (cfg.field === field) {
      nextDir = cfg.direction === 'asc' ? 'desc' : (cfg.direction === 'desc' ? null : 'asc');
    }
    if (table === 'fatal') {
      setFatalSortConfig(nextDir ? { field, direction: nextDir } : { field: null, direction: null });
    } else {
      setSortConfig(nextDir ? { field, direction: nextDir } : { field: null, direction: null });
    }
  };

  const renderArrows = (cfg, field, setCfg) => {
    if (cfg.field === field && cfg.direction === 'asc') {
      return (
        <ArrowUp className={`h-3 w-3 ${arrowClass(cfg, field, 'asc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
      );
    }
    if (cfg.field === field && cfg.direction === 'desc') {
      return (
        <ArrowDown className={`h-3 w-3 -mt-0.5 ${arrowClass(cfg, field, 'desc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'desc' });}} />
      );
    }
    return (
      <>
        <ChevronsUpDown className={`h-3 w-3 ${arrowClass(cfg, field, 'none')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
      </>
    );
  };

  const renderMainArrows = (field) => renderArrows(sortConfig, field, setSortConfig);
  const renderFatalArrows = (field) => renderArrows(fatalSortConfig, field, setFatalSortConfig);
  const moduleParameterQueryFilters = buildParameterQueryFilters(buildModuleParameterFilters(filters, advancedFilters));

  const displayConversations = useMemo(() => (
    sortConfig.field ? sortArray(filteredConversations, sortConfig.field, sortConfig.direction) : filteredConversations
  ), [filteredConversations, sortConfig]);

  const displayFatalCalls = useMemo(() => (
    fatalSortConfig.field ? sortArray(fatalCalls, fatalSortConfig.field, fatalSortConfig.direction) : fatalCalls
  ), [fatalCalls, fatalSortConfig]);

  // Effect to fetch data whenever page or fatal toggle changes
  useEffect(() => {
    if (!client_id || !service_id) return;
    if (!resolveRequestEmail()) return;
    if (segment && (!segmentFiltersReady || isInitializingRef.current)) return;
    if (showFatalCalls) {
      getFatalCalls(page);
    } else {
      get_calls_page_details(client_id, service_id, page);
    }
  }, [client_id, service_id, email, emailProp, showFatalCalls, page, segment, segmentFiltersReady]);

  // Debounce API calls when filters change to avoid rapid requests while typing
  useEffect(() => {
    if (!client_id || !service_id) return;

    // Skip debounce logic on initial mount to avoid duplicate first fetch
    if (firstFiltersLoadRef.current) {
      firstFiltersLoadRef.current = false;
      return;
    }

    // Skip API calls during initialization to prevent multiple calls on page reload
    if (isInitializingRef.current) {
      return;
    }

    // Clear previous debounce timer
    if (filtersDebounceRef.current) {
      clearTimeout(filtersDebounceRef.current);
    }

    filtersDebounceRef.current = setTimeout(() => {
      // Only reset to first page; page effect will handle fetching once
      if (page !== 1) {
        setPage(1);
      } else {
          if (showFatalCalls) {
            getFatalCalls(1);
          } else {
            get_calls_page_details(client_id, service_id, 1);
          }
      }
    }, 600); // idle delay

    return () => {
      if (filtersDebounceRef.current) {
        clearTimeout(filtersDebounceRef.current);
      }
    };
  }, [filters, advancedFilters, client_id, service_id]);


  const getFatalCalls = async (page) => {
    try {
      setLoading(true)
      const requestEmail = resolveRequestEmail();
      if (!requestEmail) return;
      let res = await server.getFatalCalls(client_id, service_id, requestEmail, page, 'conversations', undefined, filters.time, filters.customStart, filters.customEnd)
      if(res.status === 200){
        let resp = await res.json()
        setFatalCalls(resp.data)
        const totalRecords = resp.total_records;
        setTotalRecords(totalRecords)
        // setOriginalSize(totalRecords)
        setTotalPages(Math.ceil(totalRecords / 50));
      } else {
        setFatalCalls([])
      }
    } catch (error) {
      // leave existing rows; loading cleared in finally
    } finally {
      setLoading(false)
    }
  }

  // Helper functions for updating array items
  const updateAgentFeedback = (index, field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      agent_feedback_multiple: prev.agent_feedback_multiple.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const updateBusinessInsights = (index, field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      business_insights_multiple: prev.business_insights_multiple.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowAdvancedFilters(false);
      }
    }
    if (showAdvancedFilters) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    }
  }, [showAdvancedFilters])

  const captureRecords = async (payload) => {
    setIsDownloadModalOpen(false);
    let parametersJson = payload['parametersJson'], filterMode = payload['filterMode'], parsedIds = payload['parsedIds'];
    if (filterMode === 'conversation_ids'){
      await downloadConversations(parametersJson, filterMode, parsedIds, []);
    } else if (filterMode === 'customer_ids'){
      await downloadConversations(parametersJson, filterMode, [], parsedIds);
    } else {
      await downloadConversations(parametersJson, filterMode, [], []);
    }
  };

  return (
    <div className={`py-3 px-5 font-sans bg-gray-50 ${isEmbeddedModal ? 'flex h-full min-h-0 flex-col overflow-hidden' : ''}`}>
      {!showConversationPage && <><div className='mb-4 shrink-0'>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Conversation Records</h1>
        <p className="text-sm text-gray-500">View and manage all recorded customer conversations</p>
      </div>

      {exactCountDrilldown && appliedFilterSummary.length > 0 ? (
        <div className="mb-4 shrink-0 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3">
          <p className="text-xs text-indigo-700">
            These report filters are always enforced. Filters below can only narrow these records.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {appliedFilterSummary.map((item, index) => (
              <span
                key={`${item?.label || "filter"}-${index}`}
                className="rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs text-indigo-800"
              >
                <span className="font-medium">{item?.label || "Filter"}:</span>{" "}
                {item?.value || "-"}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      
      <QuickFiltersPanel className={isEmbeddedModal ? "shrink-0" : ""}>
      <QuickFiltersHeader>
        <button
                  onClick={() => setIsDownloadModalOpen(true)}
                  className="flex items-center px-4 py-2 rounded text-sm text-white hover:opacity-90"
                  style={{ backgroundColor: "rgb(79, 70, 229)" }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>
        <button 
          className="flex items-center text-indigo-600 text-sm"
          onClick={toggleAdvancedFilters}
        >
          <FaFilter className="mr-2" />
          Show Advanced Filters
        </button>
      </QuickFiltersHeader>
      <QuickFiltersRow>
      <input
          type="text"
          maxLength={500}
          value={filters.conversationId}
          onChange={(e) => handleQuickFilterChange(e, 'conversationId')}
          placeholder="Conversation ID"
          className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input"
        />
      <ModernTimeDropdown placeholder="Select Team" value={filters.team_name} onChange={(e) => handleQuickFilterChange(e, 'team_name')} options={dummyAdvancedFilterData.teams} isMulti={isMultiSelectField('team_name')}/>
      <ModernTimeDropdown isDisabled={role === 'employee'} placeholder="Select Employee" value={filters.employee} onChange={(e) => handleQuickFilterChange(e, 'employee')} options={dummyAdvancedFilterData.employees} isMulti={isMultiSelectField('employee')}/>
      <ModernTimeDropdown placeholder="Select Time Duration" value={filters.time} onChange={(e) => handleQuickFilterChange(e, 'time')} options={dummyFilterData.times} isMulti={isMultiSelectField('time')}/>
      <ModernTimeDropdown placeholder="Select Record Type" value={filters.record_type} onChange={(e) => handleQuickFilterChange(e, 'record_type')} options={dummyFilterData.modes} isMulti={isMultiSelectField('record_type')}/>
      <ModernTimeDropdown placeholder="Select Duration" value={advancedFilters.audioDuration} onChange={(e) => handleAdvancedInputChange(e, 'audioDuration')} options={dummyAdvancedFilterData.audioDuration} isMulti={isMultiSelectField('audioDuration')}/>
      </QuickFiltersRow>
      <div>
      {filters.time === "Custom Range" && (
            <div className="mb-2">
              <ModernDateRangePicker
                startDate={filters.customStart}
                endDate={filters.customEnd}
                rangeType={filters.customRangeType || "between"}
                maxRangeDays={90}
                placeholder="Select Time Range"
                onChange={({ startDate, endDate, rangeType }) =>
                  setFilters((prev) => ({
                    ...prev,
                    customStart: startDate,
                    customEnd: endDate,
                    customRangeType: rangeType || "between",
                  }))
                }
              />
            </div>
          )}
      </div>
      {advancedFilters.audioDuration === 'Custom Range' && (
        <div className="flex gap-4 mb-2">
          <input
            type="number"
            min={0}
            value={advancedFilters.audioDurationLower ?? ''}
            onChange={(e) =>
              handleAdvancedInputChange(
                { target: { value: e.target.value === '' ? null : Number(e.target.value) } },
                'audioDurationLower'
              )
            }
            placeholder="Audio Duration Lower Limit (sec)"
            className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input"
          />
          <input
            type="number"
            min={0}
            value={advancedFilters.audioDurationUpper ?? ''}
            onChange={(e) =>
              handleAdvancedInputChange(
                { target: { value: e.target.value === '' ? null : Number(e.target.value) } },
                'audioDurationUpper'
              )
            }
            placeholder="Audio Duration Upper Limit (sec)"
            className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input"
          />
        </div>
      )}
      {/* Filter Display */}
      <AppliedFiltersRow>
        {Object.keys(filters).map(
          (key) => {
            // Skip customStart and customEnd if time is not "Custom Range"
            if (key === 'customStart' || key === 'customEnd' || key === 'customRangeType') {
              return null;
            }
            if (key === 'time' && filters.time === 'Custom Range') {
              return null;
            }
            
            return (Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key]) && (
              <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                <span className="capitalize text-indigo-600">{key} - {Array.isArray(filters[key]) ? (filters[key].length > 2 ? `${filters[key].slice(0, 2).join(', ')} +${filters[key].length - 2} more` : filters[key].join(', ')) : filters[key]}</span>
                {((key!=='time' && key !== segmentParameterData.id) && !(role === 'employee' && key === 'employee')) && <button
                  className="ml-2 text-gray-600"
                  onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                >
                  <FaTimes />
                </button>}
              </div>
            );
          }
        )}
        {filters.time === 'Custom Range' && filters.customStart && (filters.customRangeType === "after" || filters.customEnd) && (
          <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
            <span className='text-indigo-600'>
              Time - {filters.customStart} {filters.customRangeType === "after" ? "onwards" : `to ${filters.customEnd}`}
            </span>
            <button
              className="ml-2 text-gray-600"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  time: '',
                  customRangeType: 'between',
                  customStart: '',
                  customEnd: '',
                }))
              }
            >
              <FaTimes />
            </button>
          </div>
        )}
        {/* Show advanced filters that are set (excluding defaults) */}
        {Object.entries(moduleParameterQueryFilters).map(([key, val]) => {
          if (filters[key] !== undefined) return null;
          if (key.endsWith('_lower') || key.endsWith('_upper')) return null;
          return (
            <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
              <span className='capitalize text-indigo-600'>{key} - {String(val)}</span>
              <button
                className="ml-2 text-gray-600"
                onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
              >
                <FaTimes />
              </button>
            </div>
          );
        })}
        {Array.from(
          new Set(
            Object.keys(moduleParameterQueryFilters)
              .filter((key) => key.endsWith('_lower') || key.endsWith('_upper'))
              .map((key) => key.replace(/_(lower|upper)$/, ''))
          )
        ).map((baseKey) => {
          const lower = moduleParameterQueryFilters[`${baseKey}_lower`];
          const upper = moduleParameterQueryFilters[`${baseKey}_upper`];
          if (lower === undefined && upper === undefined) return null;
          return (
            <div key={`${baseKey}-range`} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
              <span className='text-indigo-600'>{baseKey} - {String(lower ?? '')} to {String(upper ?? '')}</span>
              <button
                className="ml-2 text-gray-600"
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    [`${baseKey}_lower`]: '',
                    [`${baseKey}_upper`]: '',
                  }))
                }
              >
                <FaTimes />
              </button>
            </div>
          );
        })}
        {Object.entries(advancedFilters).map(([key, val]) => {
          // skip if blank or default
          if (
            val === '' || val== null
          ) {
            return null;
          }
          if (key === 'scoreLower' || key === 'scoreUpper' || key === 'audioDurationLower' || key === 'audioDurationUpper') {
            return null;
          }
          if (key === 'Call Distribution') {
            return null;
          }
          if (key === 'audioDuration' && advancedFilters.audioDuration === 'Custom Range') {
            return null;
          }
          if (shouldHideCrmLeadCreatedAtChipKey(key)) {
            return null;
          }
          if (key === 'agent_feedback_multiple' || key === 'business_insights_multiple' || key === 'custom_module_multiple' || key === 'custom_module_parameter_name' || key === 'custom_module_parameter_answer') {
            return null;
          }
          if(key !== 'agent_feedback_multiple' && key !== 'business_insights_multiple'){
          return (
            <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
              <span className='capitalize text-indigo-600'>{key} - {String(!isNaN(val/1) ? Number(val).toFixed(0) : val)}</span>
              <button
                className="ml-2 text-gray-600"
                onClick={() =>
                  setAdvancedFilters((prev) =>
                    shouldHideCrmLeadCreatedAtChipKey(key)
                      ? clearCrmLeadCreatedAtFields(prev)
                      : { ...prev, [key]: null }
                  )
                }
              >
                <FaTimes />
              </button>
            </div>
          );
          }
        })}
        {advancedFilters['Call Distribution'] && (
          <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
            <span className='text-indigo-600'>Call Distribution - {advancedFilters['Call Distribution']}</span>
            <button
              className="ml-2 text-gray-600"
              onClick={() =>
                setAdvancedFilters((prev) => ({
                  ...prev,
                  'Call Distribution': '',
                  scoreLower: null,
                  scoreUpper: null,
                }))
              }
            >
              <FaTimes />
            </button>
          </div>
        )}
        {advancedFilters.scoreLower !== null && advancedFilters.scoreLower !== '' && advancedFilters.scoreUpper !== null && advancedFilters.scoreUpper !== '' && !advancedFilters['Call Distribution'] && (
          <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
            <span className='text-indigo-600'>Overall Score - {advancedFilters.scoreLower} to {advancedFilters.scoreUpper}</span>
            <button
              className="ml-2 text-gray-600"
              onClick={() => setAdvancedFilters((prev) => ({ ...prev, scoreLower: null, scoreUpper: null }))}
            >
              <FaTimes />
            </button>
          </div>
        )}
        {advancedFilters.audioDuration === 'Custom Range' && advancedFilters.audioDurationLower !== null && advancedFilters.audioDurationUpper !== null && (
          <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
            <span className='text-indigo-600'>Audio Duration - {advancedFilters.audioDurationLower} to {advancedFilters.audioDurationUpper}</span>
            <button
              className="ml-2 text-gray-600"
              onClick={() => setAdvancedFilters((prev) => ({ ...prev, audioDuration: '', audioDurationLower: null, audioDurationUpper: null }))}
            >
              <FaTimes />
            </button>
          </div>
        )}
        <CrmLeadCreatedAtFilterChips
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
        />
      </AppliedFiltersRow>
      {!exactCountDrilldown ? <div className='flex items-center justify-end w-full'>
          <input type="checkbox" onChange={(e)=>setShowFatalCalls(e.target.checked)}/>
          <label className='px-2 py-1.5 text-sm font-medium text-gray-700 focus:outline-none'>Show Error Cases</label>
        </div> : null}
    </QuickFiltersPanel>
    
    {/* Advanced Filters Sidebar */}
    <CallAdvancedFiltersPanel
      isOpen={showAdvancedFilters}
      onClose={toggleAdvancedFilters}
      multiSelectFields={multiSelectFields}
      advancedFilters={advancedFilters}
      setAdvancedFilters={setAdvancedFilters}
      handleAdvancedInputChange={handleAdvancedInputChange}
      handleNumberInputChange={handleNumberInputChange}
      dummyAdvancedFilterData={dummyAdvancedFilterData}
      expandedSections={expandedSections}
      setExpandedSections={setExpandedSections}
      updateAgentFeedback={updateAgentFeedback}
      updateBusinessInsights={updateBusinessInsights}
      getBusinessInsightKeys={getBusinessInsightKeys}
      getBusinessInsightValues={getBusinessInsightValues}
      handleConcernCategoryChange={handleConcernCategoryChange}
      getConcernCategoryKeys={getConcernCategoryKeys}
      getConcernSubcategoriesForCategory={getConcernSubcategoriesForCategory}
      handleQueryCategoryChange={handleQueryCategoryChange}
      getQueryCategoryKeys={getQueryCategoryKeys}
      getQuerySubcategoriesForCategory={getQuerySubcategoriesForCategory}
      handleDispositionChange={handleDispositionChange}
      getDispositionKeys={getDispositionKeys}
      getSubdispositionsForDisposition={getSubdispositionsForDisposition}
      handleCustomModuleParameterChange={handleCustomModuleParameterChange}
      getCustomModuleKeys={getCustomModuleKeys}
      getCustomModuleValues={getCustomModuleValues}
      showCampaignBatchId={true}
      showNeedsReview={true}
      crmStageKey="crm_lead_stage"
      crmSubStageKey="crm_lead_sub_stage"
      showCrmLeadCreatedAt={true}
      actorTypeLabel="Agent Type"
    />
    <CallDownloadParametersModal
      quickOnly={Boolean(exactCountDrilldown)}
      filterMode={filterMode}
      setFilterMode={setFilterMode}
      setConversationIds={setConversationIds}
      setCustomerIds={setCustomerIds}
      isOpen={isDownloadModalOpen}
      onClose={() => setIsDownloadModalOpen(false)}
      quickFilterApproxCount={totalRecords}
      onApply={captureRecords}
    />

    
    
    {/* Table */}
    <div
      ref={tableScrollRef}
      className={`bg-white rounded-lg shadow-sm ${isEmbeddedModal ? 'min-h-0 flex-1 overflow-auto' : ''}`}
      style={isEmbeddedModal ? undefined : {height: "50vh", overflowY: "auto"}}
    >
      {
        loading ? (
            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </div>
        ) : showFatalCalls ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead style={{ position: "sticky", top: "0"}}>
              <tr>
                <th scope="col" aria-sort={getAriaSort(fatalSortConfig, 'id')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('id', 'fatal')}>
                    <span>Conversation ID</span>
                    <span className="ml-1 flex flex-col leading-none">{renderFatalArrows('id')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(fatalSortConfig, 'customerid')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('customerid', 'fatal')}>
                    <span>Customer ID</span>
                    <span className="ml-1 flex flex-col leading-none">{renderFatalArrows('customerid')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(fatalSortConfig, 'status')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('status', 'fatal')}>
                    <span>Status</span>
                    <span className="ml-1 flex flex-col leading-none">{renderFatalArrows('status')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(fatalSortConfig, 'error_type')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('error_type', 'fatal')}>
                    <span>Error Type</span>
                    <span className="ml-1 flex flex-col leading-none">{renderFatalArrows('error_type')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(fatalSortConfig, 'error_description')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('error_description', 'fatal')}>
                    <span>Error Description</span>
                    <span className="ml-1 flex flex-col leading-none">{renderFatalArrows('error_description')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(fatalSortConfig, 'record_time')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('record_time', 'fatal')}>
                    <span>Time</span>
                    <span className="ml-1 flex flex-col leading-none">{renderFatalArrows('record_time')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(fatalSortConfig, 'processedat')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('processedat', 'fatal')}>
                    <span>Processed At</span>
                    <span className="ml-1 flex flex-col leading-none">{renderFatalArrows('processedat')}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayFatalCalls.map((convo) => (
                <tr key={convo.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 text-blue-600 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.id}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.customerid ?? convo.customer_id ?? ''}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.status || 'Failed'}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.error_type || ''}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.error_description || ''}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatEpochToReadableDate(convo.record_time)}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatEpochToReadableDate(convo.processedat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead style={{ position: "sticky", top: "0"}}>
              <tr>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'id')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('id', 'main')}>
                    <span>Conversation ID</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('id')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'customerid')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('customerid', 'main')}>
                    <span>Customer ID</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('customerid')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'employee')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('employee', 'main')}>
                    <span>Employee Name</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('employee')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'time')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('time', 'main')}>
                    <span>Time</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('time')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'conversation_type')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('conversation_type', 'main')}>
                    <span>Type</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('conversation_type')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'mode')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('mode', 'main')}>
                    <span>Record Type</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('mode')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'duration')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('duration', 'main')}>
                    <span>Duration</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('duration')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'score')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('score', 'main')}>
                    <span>Score</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('score')}</span>
                  </button>
                </th>
                <th scope="col" aria-sort={getAriaSort(sortConfig, 'fatal')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                  <button type="button" className="flex items-center group" onClick={() => cycleSort('fatal', 'main')}>
                    <span>Fatal</span>
                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('fatal')}</span>
                  </button>
                </th>
                <th scope="col" className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayConversations.map((convo) => (
                <tr key={convo.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 text-blue-600 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.id}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.customerid }</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.employee}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatEpochToReadableDate(convo.time)}</td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.conversation_type}</td>
                  <td className="px-4 py-3 text-sm flex items-center capitalize" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                    {getModeIcon(convo.mode)} <span className="ml-1" >{convo.mode ? convo.mode : 'Call'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.duration}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                      {getScoreBar(convo.score)}
                      <span className="text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.score}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem", display: "flex", alignItems: "center"}}>
                    {/* {convo.fatal ? 
                      <FaTimes className="text-red-500" /> : ''
                    } */}
                    <span className="ml-1" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{convo.fatal}</span>
                  </td>
                  <td onClick={()=>handleCallDetail(convo.id)} className="px-4 py-3 text-blue-600 text-sm" style={{fontSize: "0.7rem", lineHeight: "1rem", cursor: "pointer"}}>View Details</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
    
    {/* Pagination */}
    <div className="shrink-0 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <div onClick={handlePreviousPage} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                Previous
              </div>
              <div onClick={handleNextPage} className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                Next
              </div>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{totalRecords === 0 ? 0 : (50 * (page - 1)) + 1}</span>
                  {" "}to <span className="font-medium">{Math.min(totalRecords, page * 50)}</span>
                  {" "}of <span className="font-medium">{totalRecords}</span> records
                </p>
              </div>
              <div>
                <nav className="z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <div onClick={handlePreviousPage} className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                    Previous
                  </div>
                  <div onClick={handleNextPage} className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                    Next
                  </div>
                </nav>
              </div>
            </div>
          </div></>}
      {
        showConversationPage && (
          <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
                        <div style={{position: "fixed", left: "2%", top: "3.5%", width: "96%", height: "93%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                        zIndex: "999999999"}}>
                            <div style={{position: "absolute", right: "10px", top: "10px", cursor: "pointer"}} onClick={handleClose}><FaTimes style={{color: "slategray"}}/></div>
                            <div style={{overflow: "auto", height: "100%", borderRadius: "10px"}}>
          <CallDetails selectedOption={service_id} open_type={handleClose} callIdProp={selectedCallID} selectedClientId={client_id}/>
          </div>
          </div>
          </div>
        )
      }
      <style jsx>
          {`
          .sections-headings {
                          cursor: pointer;
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          padding: 15px;
                          border-radius: 10px;
                          transition: background 0.3s;
                          width: 100%;
                          }

                          .section-title {
                          font-size: 15px;
                          font-weight: 500;
                          display: flex;
                          align-items: center;
                          }
          `}              
        </style>
  </div>
);
};

export default ConversationRecords;
